/**
 * Player Token utility for managing unique session tokens
 */

const LS_KEY_PLAYER_TOKEN = 'treasureHunt_playerToken';

/**
 * Generates a unique player token using timestamp and random values
 * @returns {string} Unique player token
 */
const generatePlayerToken = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2, 9);
    const sessionId = Math.random().toString(36).substr(2, 5);

    return `player_${timestamp}_${randomStr}_${sessionId}`;
};

/**
 * Gets the current player token from localStorage or creates a new one
 * @returns {string} Player token for this session
 */
const getPlayerToken = () => {
    try {
        let token = localStorage.getItem(LS_KEY_PLAYER_TOKEN);

        if (!token) {
            token = generatePlayerToken();
            localStorage.setItem(LS_KEY_PLAYER_TOKEN, token);
        }

        return token;
    } catch (error) {
        console.warn('Failed to access localStorage for player token:', error);
        // Fallback to session-only token
        return generatePlayerToken();
    }
};

/**
 * Creates a new player token and stores it
 * @returns {string} New player token
 */
const createNewPlayerToken = () => {
    const token = generatePlayerToken();

    try {
        localStorage.setItem(LS_KEY_PLAYER_TOKEN, token);
    } catch (error) {
        console.warn('Failed to store player token in localStorage:', error);
    }

    return token;
};

/**
 * Clears the stored player token (useful for testing or logout)
 */
const clearPlayerToken = () => {
    try {
        localStorage.removeItem(LS_KEY_PLAYER_TOKEN);
    } catch (error) {
        console.warn('Failed to clear player token from localStorage:', error);
    }
};

/**
 * Validates if a token looks like a valid player token
 * @param {string} token - Token to validate
 * @returns {boolean} True if token appears valid
 */
const isValidPlayerToken = (token) => {
    return typeof token === 'string' &&
        token.startsWith('player_') &&
        token.length > 20; // Basic length check
};

const playerTokenUtils = {
    generatePlayerToken,
    getPlayerToken,
    createNewPlayerToken,
    clearPlayerToken,
    isValidPlayerToken,
    LS_KEY_PLAYER_TOKEN
};

export default playerTokenUtils;